/**
 * Routines Client (Routine Threads, 2026-07-31)
 *
 * HTTP client for the backend routine-management endpoint. All calls are
 * authenticated with the Firebase ID token (same pattern as
 * src/services/prest0nSession/client.ts).
 *
 * Endpoint:
 *   POST /prest0nRoutineAction
 *     { action: 'list' }
 *     { action: 'pause' | 'resume' | 'cancel', threadId }
 *
 * Creation and schedule EDITS are deliberately NOT here — they happen
 * conversationally in the routine thread (the product premise). This client
 * covers the manager panel's bulk controls: list, pause, resume, stop.
 */

import { getAuth } from 'firebase/auth';

// ============================================================================
// Config
// ============================================================================

const CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

/**
 * Per-request timeout. Mirrors the session client's rationale: a hung Cloud
 * Function must surface as an inline row error, never a forever-disabled
 * button. 25s clears p99 cold starts with room to spare.
 */
const ROUTINE_FETCH_TIMEOUT_MS = 25_000;

// ============================================================================
// Types
// ============================================================================

export type RoutineActionName = 'list' | 'pause' | 'resume' | 'cancel';

export interface RoutineActionRequest {
  action: RoutineActionName;
  /** Target routine id (v2 identity) — required for pause/resume/cancel. */
  routineId?: string;
  /** Legacy addressing (pre-v2 servers resolve a single-routine thread). */
  threadId?: string;
}

/**
 * One routine as returned by `list` (server RoutineDoc, client-safe subset).
 * Mirrors the exact wire shape of `prest0nRoutineAction` list rows
 * (functions/src/prest0nSession/routineEndpoints.ts). v2: a routine's
 * identity is its routineId (rtn_<uuid>; equals threadId for legacy rows —
 * the normalizer backfills routineId from threadId for deploy skew), a
 * thread can carry SEVERAL rows, `kind` separates time-based rows from
 * event-trigger rows (whose scheduleSummary is their trigger summary and
 * whose nextRunAtMs is always null), and timestamps travel as epoch-ms
 * numbers (`null` when absent).
 */
export interface RoutineSummary {
  /** The routine's stable identity — the action-targeting key. */
  routineId: string;
  /** The routine's home thread (navigation key; NOT unique across rows). */
  threadId: string;
  name: string;
  scheduleSummary: string;
  status: 'active' | 'paused' | 'completed';
  /** 'trigger' rows fire on platform events; absent/'schedule' → time-based. */
  kind?: 'schedule' | 'trigger';
  triggerEvent?: string;
  nextRunAtMs?: number | null;
  lastRunAtMs?: number | null;
  lastRunStatus?: 'ok' | 'error' | null;
  runCount?: number;
  createdAtMs?: number;
  /**
   * Liveness of the routine's thread as stamped by the server (2026-08-02):
   * the list endpoint heals/excludes archived-or-missing-thread routines
   * server-side, so every row it returns is a LIVE routine and carries
   * `threadState: 'live'`. Optional purely for deploy skew (an older server
   * omits it); the client never branches on it — absence is tolerated and
   * no client-side archived handling exists.
   */
  threadState?: string;
}

export interface RoutineActionResult {
  success: boolean;
  /** Present on `list` responses. */
  routines?: RoutineSummary[];
  /**
   * Present on pause/resume responses — the routine's POST-action state
   * (fresh `nextRunAtMs` after a resume). Null after cancel (doc deleted).
   */
  routine?: Partial<RoutineSummary> | null;
  /** Optional short factual note from the backend (state-change message). */
  message?: string;
  /** Optional human-readable error detail from the backend. */
  error?: string;
}

// ============================================================================
// Internal helpers (pattern: prest0nSession/client.ts)
// ============================================================================

/** Race a fetch against a timeout so a hung request never wedges the panel. */
async function fetchWithTimeout(
  input: RequestInfo,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<Response> {
  const { timeoutMs = ROUTINE_FETCH_TIMEOUT_MS, ...rest } = init;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs);
  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Get the current Firebase ID token for authenticated requests.
 * Throws if no user is authenticated.
 */
async function getFirebaseIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('[Routines] No authenticated user — cannot get ID token');
  }
  return user.getIdToken(false);
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Send a routine management action to `prest0nRoutineAction`. Throws on
 * non-2xx / transport failure — callers surface the message inline.
 */
export async function sendRoutineAction(request: RoutineActionRequest): Promise<RoutineActionResult> {
  const token = await getFirebaseIdToken();
  const response = await fetchWithTimeout(`${CF_BASE_URL}/prest0nRoutineAction`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`[Routines] prest0nRoutineAction failed: ${response.status} ${response.statusText} — ${text}`);
  }

  return response.json() as Promise<RoutineActionResult>;
}

/**
 * Coerce a wire field that MUST render as text into a string (adversarial
 * review finding 6, 2026-08-03). Strings pass through; finite numbers
 * stringify; everything else (null / objects / arrays / booleans) becomes ''
 * — the row builders already treat '' as "absent" (`s.name || 'Routine'`,
 * falsy scheduleSummary drops out of the meta join).
 */
function coerceWireText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** Epoch-ms wire field → number, or null for anything non-finite/absent. */
function coerceWireMs(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * WIRE VALIDATION (adversarial review finding 6, 2026-08-03).
 *
 * `list` rows used to be handed to the renderers exactly as the JSON arrived.
 * Since routine list parity (2026-08-02) that array feeds the SIDEBAR's render
 * body, not just the manager popup — so one null element or a non-string
 * `scheduleSummary` threw inside ThreadSidebar and took the whole rail down
 * instead of one popup. Normalize here, at the boundary, once:
 *   • non-object elements and elements without a non-empty string `threadId`
 *     are DROPPED (a routine's identity IS its threadId — a row without one
 *     is unrenderable and unactionable);
 *   • `name` / `scheduleSummary` are coerced to strings;
 *   • `status` is narrowed to the union ('paused' only when it literally says
 *     so — an unknown status must never masquerade as paused);
 *   • timestamp fields are coerced to number|null and `lastRunStatus` to the
 *     'ok'|'error'|null union;
 *   • additive optional fields (runCount / createdAtMs / threadState) survive
 *     only when well-typed (deploy-skew tolerance, never a branch).
 * Exported for direct pinning.
 */
export function normalizeRoutineSummaries(raw: unknown): RoutineSummary[] {
  if (!Array.isArray(raw)) return [];
  const out: RoutineSummary[] = [];
  for (const element of raw) {
    if (!element || typeof element !== 'object' || Array.isArray(element)) continue;
    const row = element as Record<string, unknown>;
    const threadId = typeof row.threadId === 'string' ? row.threadId.trim() : '';
    if (!threadId) continue;
    // v2 rows carry routineId; a legacy row's identity IS its threadId.
    const routineId =
      (typeof row.routineId === 'string' && row.routineId.trim()) ||
      (typeof row.id === 'string' && row.id.trim()) ||
      threadId;
    const summary: RoutineSummary = {
      routineId,
      threadId,
      name: coerceWireText(row.name),
      scheduleSummary: coerceWireText(row.scheduleSummary),
      status: row.status === 'paused' || row.status === 'completed' ? row.status : 'active',
      nextRunAtMs: coerceWireMs(row.nextRunAtMs),
      lastRunAtMs: coerceWireMs(row.lastRunAtMs),
      lastRunStatus:
        row.lastRunStatus === 'ok' || row.lastRunStatus === 'error' ? row.lastRunStatus : null,
    };
    if (typeof row.runCount === 'number' && Number.isFinite(row.runCount)) {
      summary.runCount = row.runCount;
    }
    if (typeof row.createdAtMs === 'number' && Number.isFinite(row.createdAtMs)) {
      summary.createdAtMs = row.createdAtMs;
    }
    if (typeof row.threadState === 'string') summary.threadState = row.threadState;
    if (row.kind === 'trigger' || row.kind === 'schedule') summary.kind = row.kind;
    if (typeof row.triggerEvent === 'string' && row.triggerEvent) summary.triggerEvent = row.triggerEvent;
    out.push(summary);
  }
  return out;
}

/** List every routine on the account (validated at the wire boundary). */
export async function listRoutines(): Promise<RoutineSummary[]> {
  const result = await sendRoutineAction({ action: 'list' });
  return normalizeRoutineSummaries(result.routines);
}

/** Pause a routine (its thread and history stay; runs stop until resumed). */
export async function pauseRoutine(routineId: string): Promise<RoutineActionResult> {
  return sendRoutineAction({ action: 'pause', routineId });
}

/** Resume a paused routine. */
export async function resumeRoutine(routineId: string): Promise<RoutineActionResult> {
  return sendRoutineAction({ action: 'resume', routineId });
}

/**
 * Stop a routine permanently. The server deletes the routine and refreshes the
 * thread's routine mirror — the conversation, its history, and the thread's
 * OTHER routines stay intact.
 */
export async function cancelRoutine(routineId: string): Promise<RoutineActionResult> {
  return sendRoutineAction({ action: 'cancel', routineId });
}
